import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { DEPGUARD_PRODUCT } from './websiteData';

const SEES_CARDS = [
  {
    title: 'Reachability changes everything',
    desc: 'Most tools stop at "you have a CVE." DepGuard traces whether your code actually calls the vulnerable function. Three tiers: static dependency graph, dynamic analysis, and code-level import tracing. The result is 80% fewer false positives — and the ones that remain are real.',
  },
  {
    title: 'Supply chain signals before the breach',
    desc: 'Seven community health metrics plus maintainer anomaly detection. Bus factor, release efficiency, contributor retention, response time. These signals catch abandoned packages and hijacked maintainers before they show up in a CVE advisory.',
  },
  {
    title: 'SBOM quality is the real gap',
    desc: 'Not just "do you have an SBOM?" but "is your SBOM actually useful?" Five weighted dimensions: completeness, identifiers, license quality, metadata, version coverage. NTIA 7-element compliance check. Grade A through F. Most SBOMs score a C or below.',
  },
];

const ANTI_PATTERNS = [
  {
    icon: '🚨',
    category: 'Alert fatigue scanners',
    gap: 'Dump 10,000 CVEs without context. Every advisory is critical. No reachability. No priority. Your team triages noise for weeks.',
    depguard: 'Reachability analysis filters down to the CVEs your code actually calls. 12,000 advisories become 340 actionable findings.',
  },
  {
    icon: '🏗️',
    category: 'Infrastructure-heavy platforms',
    gap: 'Require Kubernetes, PostgreSQL, Redis, and a DevOps team to maintain the scanner itself. The security tool becomes another infrastructure project.',
    depguard: 'Single Go binary. Embedded SQLite. No JVM, Docker, Redis, or external database. Install in one command, scanning in 60 seconds.',
  },
  {
    icon: '📏',
    category: 'Single-dimension checkers',
    gap: 'Only SCA, or only license, or only SBOM validation. Three tools, three dashboards, three alert queues. Each knows nothing about the others.',
    depguard: '16 analysis dimensions in one platform. Vulnerabilities, licenses, SBOM quality, EU CRA, SLSA provenance, community health — correlated, not siloed.',
  },
  {
    icon: '☁️',
    category: 'Cloud-only platforms',
    gap: 'Require uploading SBOMs to vendor infrastructure. Air-gapped environments, regulated industries, and privacy-conscious teams are locked out.',
    depguard: 'Self-hosted by default. Runs on a laptop, in CI, or on-premises. Your SBOMs never leave your network. Air-gapped deployments fully supported.',
  },
  {
    icon: '☑️',
    category: 'Compliance checkbox tools',
    gap: 'Claim "CRA compliant" but only check SBOM presence. No article-level mapping. No field-level validation. Auditors see through it immediately.',
    depguard: '13 automated checks mapped to specific EU CRA articles. Field-level validation: PURL, supplier, hash, dependencies, timestamps. Weighted scoring with downloadable audit document.',
  },
];

const PILLARS = [
  {
    name: 'Security Intelligence',
    icon: '🔒',
    items: [
      { icon: '🔍', title: 'Vulnerability Scanning', desc: 'OSV.dev and GHSA scanning with CVSS scoring and exploitability context. VEX statements for assessed-not-affected packages.' },
      { icon: '🧭', title: 'Reachability Analysis', desc: 'Three tiers: dependency graph, dynamic analysis, code-level import tracing. Know which CVEs are called, not just declared.' },
      { icon: '🦠', title: 'Malware & Typosquatting', desc: 'OSV MAL-* checks plus three string distance algorithms. 0.88 similarity threshold catches supply chain attacks CVE databases miss.' },
      { icon: '⚠️', title: 'Maintainer Anomaly Detection', desc: 'Flags recent maintainer changes, download spikes, unusual publish times, single-maintainer accounts without 2FA.' },
    ],
  },
  {
    name: 'Compliance & Governance',
    icon: '📋',
    items: [
      { icon: '🇪🇺', title: 'EU CRA Compliance', desc: 'Article-by-article mapping (Art. 13, 14, 20). 13 automated checks across 3 articles. Weighted scoring A through F. Downloadable audit document with attestation block.' },
      { icon: '🔐', title: 'SLSA Provenance', desc: 'Levels 0-4. DSSE envelope and in-toto format validation. Ed25519, ECDSA, RSA signature verification. Subject digest matching.' },
      { icon: '📊', title: 'SBOM Quality Scoring', desc: 'Five weighted dimensions: Completeness (30%), Identifiers (25%), License Quality (20%), Metadata (15%), Version Completeness (10%).' },
      { icon: '⚖️', title: 'License Analysis', desc: 'Per-package detection with risk categories. Multi-registry fallback: npm, PyPI, Maven, NuGet, ClearlyDefined.' },
    ],
  },
  {
    name: 'Operations',
    icon: '⚙️',
    items: [
      { icon: '🎯', title: 'Policy Engine', desc: '3 presets plus custom rules. 18+ dimensions. CI/CD gate endpoint returns pass/fail with score and violation details.' },
      { icon: '🔧', title: 'Remediation Tracking', desc: 'Assign owners and due dates. Overdue alerts fire automatically. Status tracking from identified through resolved.' },
      { icon: '📝', title: 'VEX Statements', desc: 'Status, justification, and FixedIn badges. CycloneDX VEX export. Machine-readable vulnerability assessments.' },
      { icon: '🔔', title: 'Webhooks & Alerts', desc: 'Four event types. HMAC signing. Delivery logs. Slack and Teams integration with configurable thresholds.' },
    ],
  },
  {
    name: 'Platform',
    icon: '🏛️',
    items: [
      { icon: '🤖', title: 'AI Assistant', desc: 'Five providers: Copilot SDK, GitHub Models, OpenAI, Anthropic, Ollama. Natural language queries on live SBOM data.' },
      { icon: '🕸️', title: 'Portfolio Analytics', desc: 'Cross-project blast radius. "This CVE affects 23 of your 150 repos." Package search across all SBOMs.' },
      { icon: '💥', title: 'Cross-Project Impact', desc: 'Supplier trust scoring. Compliance matrix with CSV export. Portfolio-wide risk posture at a glance.' },
      { icon: '🏥', title: 'Community Health', desc: 'Seven GitHub signals: bus factor, BMI, REI, lead time, retention, growth, response. Weighted scoring per package.' },
    ],
  },
];

const COMPARISON_ROWS = [
  { feature: 'Reachability analysis', depguard: '✅ 3-tier', alertScanners: '❌', licenseCheckers: '❌', sbomValidators: '❌', cloudSCA: 'Partial' },
  { feature: 'EU CRA article-level audit', depguard: '✅ 13 checks', alertScanners: '❌', licenseCheckers: '❌', sbomValidators: 'Partial', cloudSCA: '❌' },
  { feature: 'Community health (7 metrics)', depguard: '✅', alertScanners: '❌', licenseCheckers: '❌', sbomValidators: '❌', cloudSCA: 'Partial' },
  { feature: 'Malware + typosquat detection', depguard: '✅ 3 algorithms', alertScanners: 'Partial', licenseCheckers: '❌', sbomValidators: '❌', cloudSCA: 'Partial' },
  { feature: 'SLSA provenance verification', depguard: '✅ L0-L4', alertScanners: '❌', licenseCheckers: '❌', sbomValidators: '❌', cloudSCA: 'Partial' },
  { feature: 'SBOM quality scoring', depguard: '✅ 5 dimensions', alertScanners: '❌', licenseCheckers: '❌', sbomValidators: 'Partial', cloudSCA: '❌' },
  { feature: 'Policy engine with CI/CD gate', depguard: '✅ 18+ rules', alertScanners: 'Partial', licenseCheckers: '❌', sbomValidators: '❌', cloudSCA: '✅' },
  { feature: 'Multi-provider AI assistant', depguard: '✅ 5 providers', alertScanners: '❌', licenseCheckers: '❌', sbomValidators: '❌', cloudSCA: '❌' },
  { feature: 'Self-hosted / air-gapped', depguard: '✅', alertScanners: 'Partial', licenseCheckers: '✅', sbomValidators: '✅', cloudSCA: '❌' },
  { feature: 'Zero infrastructure', depguard: '✅ SQLite', alertScanners: '❌', licenseCheckers: '✅', sbomValidators: '✅', cloudSCA: '❌' },
  { feature: 'Portfolio blast radius', depguard: '✅', alertScanners: 'Partial', licenseCheckers: '❌', sbomValidators: '❌', cloudSCA: '✅' },
  { feature: 'VEX statement management', depguard: '✅ CycloneDX', alertScanners: '❌', licenseCheckers: '❌', sbomValidators: '❌', cloudSCA: 'Partial' },
];

const DEEP_DIVE_TABS = [
  {
    id: 'sbom',
    label: 'SBOM Engine',
    content: 'Dual-format parsing: SPDX-JSON and CycloneDX-JSON with automatic format detection. PURL normalization strips qualifiers for consistent matching. CPE derivation from PURL when CPE is missing. Ecosystem-specific manifest parsing for Maven (pom.xml), npm (package-lock.json), PyPI (requirements.txt), NuGet (.csproj), RubyGems (Gemfile.lock), Go (go.sum), and Swift (Package.resolved). Conservative enrichment — DepGuard adds identifiers when the mapping is unambiguous, never when it requires guessing.',
  },
  {
    id: 'policy',
    label: 'Policy Engine',
    content: '18+ rule dimensions across three preset profiles: Strict (zero tolerance), Moderate (balanced), and Permissive (advisory only). Rule dimensions include: NoMalicious, MaxAdvisories, ForbiddenLicenses, MaxOutdatedPercent, NoAbandoned, RequirePinned, MinScorecardScore, MinCRAScore, MaxMissingPURLPercent, MaxMissingHashesPercent, and more. Violation scoring formula: each failing dimension adds weighted points. CI/CD gate endpoint: GET /policy/{id}/gate returns { passed, score, violations[] }. Integrates with GitHub Actions, Azure Pipelines, and GitLab CI.',
  },
  {
    id: 'cra',
    label: 'EU CRA Compliance',
    content: 'Article-level mapping to EU CRA obligations: Art. 13 (product requirements), Art. 14 (vulnerability handling), Art. 20 (SBOM requirements). 13 automated checks validate: creation timestamp and author metadata, manufacturer/supplier identity, PURL identifier coverage, supplier field coverage, cryptographic hash presence (SHA-256+), dependency relationship completeness, license disclosure, component name and version presence, vulnerability-free status (Art. 13), and patch timeline enforcement (Art. 14). Weighted scoring produces a grade A through F with gap analysis showing exactly which fields are missing and which articles they map to.',
  },
  {
    id: 'ai',
    label: 'AI Architecture',
    content: 'Five provider backends: GitHub Copilot SDK (default), GitHub Models, OpenAI, Anthropic, and Ollama (local/air-gapped). Natural language to SQLite query translation — ask questions about your SBOMs in plain English and get results from live data. Autonomous triage mode: when a new CVE is published against a package in your portfolio, the AI proposes VEX statements, evaluates reachability, opens remediation items, and sends webhook alerts — without human input. Remediation advice includes version upgrade paths, breaking change analysis, and alternative package suggestions.',
  },
];

const DECISION_CARDS = [
  {
    icon: '🇪🇺',
    big: 'EU CRA audit prep',
    desc: 'Expected a quarter. Closed in one meeting. 13 automated checks mapped to Art. 13, 14, and 20. The audit document downloads as a PDF with attestation block.',
  },
  {
    icon: '📉',
    big: '12,000 → 340',
    desc: 'CVEs to actual findings. Reachability filtering removes the noise. Your team fixes real vulnerabilities instead of triaging alerts for weeks.',
  },
  {
    icon: '💰',
    big: 'Replace 4 tools',
    desc: 'SCA scanner, license checker, compliance tool, SBOM validator — one binary replaces all four. No infrastructure cost. No per-seat licensing.',
  },
  {
    icon: '⚡',
    big: '60 seconds',
    desc: 'From SBOM upload to full risk report with CI/CD gate decision. Not "fast for a security tool." Actually fast.',
  },
];

const ECOSYSTEMS = [
  { name: 'Maven', icon: '☕', registry: 'Maven Central', manifest: 'pom.xml', desc: 'Java and Kotlin ecosystem. Transitive dependency resolution. Parent POM inheritance.' },
  { name: 'npm', icon: '📦', registry: 'npmjs.com', manifest: 'package-lock.json', desc: 'JavaScript and TypeScript. Lock file parsing for exact version resolution. Workspace support.' },
  { name: 'PyPI', icon: '🐍', registry: 'pypi.org', manifest: 'requirements.txt', desc: 'Python ecosystem. Supports pip freeze, Poetry lock, and Pipenv formats.' },
  { name: 'NuGet', icon: '🔷', registry: 'nuget.org', manifest: '.csproj', desc: '.NET ecosystem. PackageReference and packages.config parsing.' },
  { name: 'RubyGems', icon: '💎', registry: 'rubygems.org', manifest: 'Gemfile.lock', desc: 'Ruby ecosystem. Bundler lock file parsing with platform detection.' },
  { name: 'Go', icon: '🐹', registry: 'proxy.golang.org', manifest: 'go.sum', desc: 'Go modules. Checksum database verification. Replace directive handling.' },
  { name: 'Swift', icon: '🍎', registry: 'Swift Package Index', manifest: 'Package.resolved', desc: 'Swift Package Manager. Version pin resolution from Package.resolved.' },
];

const HEALTH_METRICS = [
  { name: 'Bus Factor Score', weight: '20%', desc: 'Contributors needed for 80% of commits. Low bus factor = high maintainer risk.' },
  { name: 'BMI — Bug Management Index', weight: '20%', desc: 'Percentage of issues closed. Measures whether maintainers actually manage the backlog.' },
  { name: 'REI — Release Efficiency Index', weight: '15%', desc: 'Commits per release. High REI means big, risky releases. Low means frequent, safe ones.' },
  { name: 'Lead Time Score', weight: '15%', desc: 'Median PR merge time. Slow merges signal maintainer fatigue or project decline.' },
  { name: 'Retention Rate', weight: '15%', desc: 'Quarter-over-quarter contributor retention. Declining retention precedes abandonment.' },
  { name: 'Growth Score', weight: '10%', desc: 'Commit trend slope over the last year. Flat or negative means the project is stalling.' },
  { name: 'Response Score', weight: '5%', desc: 'Median issue response time. No response after 30 days? The project may be abandoned.' },
];

const CLI_COMMANDS = [
  { cmd: 'curl -X POST .../sbom/my-app/upload -F file=@sbom.json', purpose: 'Upload an SBOM (SPDX or CycloneDX)' },
  { cmd: 'curl .../sbom/my-app/latest/vulnerabilities', purpose: 'Scan for vulnerabilities via OSV.dev' },
  { cmd: 'curl .../sbom/my-app/latest/cra', purpose: 'Run EU CRA compliance checks' },
  { cmd: 'curl .../sbom/my-app/latest/quality', purpose: 'Get SBOM quality score (A-F)' },
  { cmd: 'curl .../sbom/my-app/latest/policy/strict/gate', purpose: 'CI/CD gate — pass or fail' },
  { cmd: 'curl .../sbom/my-app/latest/health', purpose: 'Community health metrics per package' },
  { cmd: 'curl .../sbom/my-app/latest/reachability', purpose: 'Reachability analysis for CVEs' },
  { cmd: 'curl .../sbom/my-app/latest/malware', purpose: 'Malware and typosquat detection' },
];

export default function WebsiteDepGuardPage() {
  const { icon, accentColor, accentRgb, tagline, heroTitle, heroLead, features } = DEPGUARD_PRODUCT;
  const [activeTab, setActiveTab] = useState('sbom');
  const [expandedPillar, setExpandedPillar] = useState(0);

  return (
    <div className="dgw-page-wrap">
      {/* ── Hero ── */}
      <section style={{
        padding: '80px clamp(20px,5vw,60px) 60px',
        background: '#04080f',
        borderBottom: '1px solid rgba(255,255,255,.08)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: '-80px', left: '50%', transform: 'translateX(-50%)',
          width: 700, height: 500, borderRadius: '50%',
          background: `radial-gradient(circle, rgba(${accentRgb},.12), transparent 70%)`,
          pointerEvents: 'none',
        }} />
        <div style={{ maxWidth: 1280, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div className="dgw-2col" style={{ gap: 48, alignItems: 'center' }}>
            {/* Left */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
                <span style={{ fontSize: 40 }}>{icon}</span>
                <span style={{
                  fontSize: 12, fontWeight: 700, letterSpacing: '.8px', textTransform: 'uppercase',
                  color: accentColor,
                  background: `rgba(${accentRgb},.1)`,
                  border: `1px solid rgba(${accentRgb},.25)`,
                  padding: '5px 14px', borderRadius: 100,
                }}>{tagline}</span>
              </div>
              <h1 style={{
                fontSize: 'clamp(36px,5vw,60px)', fontWeight: 800, color: '#fff',
                letterSpacing: '-2px', lineHeight: 1.05, marginBottom: 20,
              }}>
                {heroTitle}
              </h1>
              <p style={{ fontSize: 17, color: '#94a3b8', lineHeight: 1.7, marginBottom: 32, maxWidth: 560 }}>
                {heroLead}
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Link to="/trial?plan=professional&product=depguard" className="dgw-btn dgw-btn-primary dgw-btn-lg">
                  Start free trial →
                </Link>
                <Link to="/website/contact?type=demo" className="dgw-btn dgw-btn-ghost dgw-btn-lg">
                  Book demo
                </Link>
              </div>
            </div>
            {/* Right — accent panel */}
            <div style={{
              padding: 32, borderRadius: 16,
              background: `rgba(${accentRgb},.06)`,
              border: `1px solid rgba(${accentRgb},.2)`,
              display: 'flex', flexDirection: 'column', gap: 16,
            }}>
              {features.slice(0, 3).map(f => (
                <div key={f.title} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 20, flexShrink: 0 }}>{f.icon}</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#e6edf3', marginBottom: 3 }}>{f.title}</div>
                    <div style={{ fontSize: 13, color: '#8b949e', lineHeight: 1.5 }}>{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Stats bar */}
          <div style={{
            display: 'flex', gap: 0, justifyContent: 'center', flexWrap: 'wrap',
            background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)',
            borderRadius: 14, padding: '16px 32px',
            maxWidth: 860, margin: '48px auto 0',
          }}>
            {[
              { val: '13', label: 'analysis dimensions' },
              { val: '80+', label: 'API endpoints' },
              { val: '3', label: 'compliance frameworks' },
              { val: '5', label: 'AI providers' },
              { val: '0', label: 'infrastructure needed' },
            ].map((s, i) => (
              <div key={s.label} style={{
                flex: 1, minWidth: 120, textAlign: 'center',
                padding: '0 20px',
                borderLeft: i > 0 ? '1px solid rgba(255,255,255,.08)' : 'none',
              }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: accentColor, letterSpacing: '-1px' }}>{s.val}</div>
                <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.6px', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '80px clamp(20px,5vw,60px)' }}>

        {/* ── What DepGuard sees that others miss ── */}
        <div style={{ marginBottom: 100 }}>
          <div className="dgw-section-header" style={{ marginBottom: 48 }}>
            <span className="dgw-section-tag" style={{ color: accentColor, background: `rgba(${accentRgb},.1)`, borderColor: `rgba(${accentRgb},.25)` }}>Beyond scanning</span>
            <h2 style={{ color: 'var(--dgw-text)' }}>What DepGuard sees that others miss</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 24 }}>
            {SEES_CARDS.map(c => (
              <div key={c.title} className="dgw-card" style={{ padding: '32px 28px' }}>
                <h4 style={{ color: 'var(--dgw-text)', fontSize: 18, fontWeight: 800, marginBottom: 12 }}>{c.title}</h4>
                <p style={{ color: 'var(--dgw-muted)', fontSize: 15, lineHeight: 1.7, margin: 0 }}>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── What makes DepGuard different (anti-patterns) ── */}
        <div style={{ marginBottom: 100 }}>
          <div className="dgw-section-header" style={{ marginBottom: 48 }}>
            <span className="dgw-section-tag" style={{ color: accentColor, background: `rgba(${accentRgb},.1)`, borderColor: `rgba(${accentRgb},.25)` }}>Anti-patterns</span>
            <h2 style={{ color: 'var(--dgw-text)' }}>What makes DepGuard different</h2>
            <p className="dgw-section-lead">
              Most supply chain tools solve one concern and ignore the rest. DepGuard correlates 16 analysis dimensions
              into a single risk picture — because a CVE in an abandoned package with a copyleft license is a different problem
              than a CVE in a healthy, permissively licensed library.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {ANTI_PATTERNS.map(cat => (
              <div key={cat.category} style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0,
                borderRadius: 14, overflow: 'hidden',
                border: '1px solid rgba(255,255,255,.08)',
              }}>
                <div style={{ padding: '24px 28px', background: 'rgba(255,255,255,.02)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <span style={{ fontSize: 20 }}>{cat.icon}</span>
                    <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--dgw-text)' }}>{cat.category}</span>
                  </div>
                  <p style={{ fontSize: 14, color: '#ef4444', lineHeight: 1.6, margin: 0, opacity: .8 }}>{cat.gap}</p>
                </div>
                <div style={{ padding: '24px 28px', background: `rgba(${accentRgb},.04)`, borderLeft: '1px solid rgba(255,255,255,.06)' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: accentColor, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>DepGuard</div>
                  <p style={{ fontSize: 14, color: 'var(--dgw-muted)', lineHeight: 1.6, margin: 0 }}>{cat.depguard}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── 16 Analysis Dimensions (4 pillars) ── */}
        <div style={{ marginBottom: 100 }}>
          <div className="dgw-section-header" style={{ marginBottom: 48 }}>
            <span className="dgw-section-tag" style={{ color: accentColor, background: `rgba(${accentRgb},.1)`, borderColor: `rgba(${accentRgb},.25)` }}>Capabilities</span>
            <h2 style={{ color: 'var(--dgw-text)' }}>16 analysis dimensions. Four pillars.</h2>
            <p className="dgw-section-lead">
              Every dimension feeds the same risk model. A package with a critical CVE, a declining community,
              and a missing PURL scores differently than one with just a CVE. Context changes priority.
            </p>
          </div>
          {PILLARS.map((pillar, pi) => (
            <div key={pillar.name} style={{ marginBottom: 24 }}>
              <button
                onClick={() => setExpandedPillar(expandedPillar === pi ? -1 : pi)}
                style={{
                  width: '100%', textAlign: 'left', cursor: 'pointer',
                  padding: '18px 24px', borderRadius: expandedPillar === pi ? '14px 14px 0 0' : 14,
                  background: expandedPillar === pi ? `rgba(${accentRgb},.08)` : 'rgba(255,255,255,.03)',
                  border: `1px solid ${expandedPillar === pi ? `rgba(${accentRgb},.25)` : 'rgba(255,255,255,.08)'}`,
                  borderBottom: expandedPillar === pi ? 'none' : undefined,
                  display: 'flex', alignItems: 'center', gap: 14,
                  transition: 'all .2s',
                }}
              >
                <span style={{ fontSize: 24 }}>{pillar.icon}</span>
                <span style={{ fontSize: 17, fontWeight: 800, color: expandedPillar === pi ? accentColor : 'var(--dgw-text)', flex: 1 }}>{pillar.name}</span>
                <span style={{ fontSize: 12, color: '#64748b' }}>{pillar.items.length} dimensions</span>
                <span style={{ fontSize: 18, color: '#64748b', transform: expandedPillar === pi ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>▼</span>
              </button>
              {expandedPillar === pi && (
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 16,
                  padding: 24,
                  borderRadius: '0 0 14px 14px',
                  background: `rgba(${accentRgb},.04)`,
                  border: `1px solid rgba(${accentRgb},.25)`,
                  borderTop: 'none',
                }}>
                  {pillar.items.map(item => (
                    <div key={item.title} style={{
                      padding: '22px 20px', borderRadius: 12,
                      background: 'rgba(255,255,255,.03)',
                      border: '1px solid rgba(255,255,255,.08)',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                        <span style={{ fontSize: 20 }}>{item.icon}</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--dgw-text)' }}>{item.title}</span>
                      </div>
                      <p style={{ fontSize: 13, color: 'var(--dgw-muted)', lineHeight: 1.6, margin: 0 }}>{item.desc}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ── Community Health Deep Dive ── */}
        <div style={{ marginBottom: 100 }}>
          <div className="dgw-section-header" style={{ marginBottom: 48 }}>
            <span className="dgw-section-tag" style={{ color: accentColor, background: `rgba(${accentRgb},.1)`, borderColor: `rgba(${accentRgb},.25)` }}>Package intelligence</span>
            <h2 style={{ color: 'var(--dgw-text)' }}>Seven signals. One health score.</h2>
            <p className="dgw-section-lead">
              CVE databases tell you what went wrong. Community health metrics tell you what is going wrong.
              A package with declining retention and zero releases in 18 months is a supply chain risk — even with no known CVEs.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 16 }}>
            {HEALTH_METRICS.map(m => (
              <div key={m.name} style={{
                padding: '22px 24px', borderRadius: 14,
                background: `rgba(${accentRgb},.04)`,
                border: `1px solid rgba(${accentRgb},.15)`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--dgw-text)' }}>{m.name}</span>
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: accentColor,
                    background: `rgba(${accentRgb},.1)`,
                    border: `1px solid rgba(${accentRgb},.25)`,
                    padding: '2px 8px', borderRadius: 20,
                  }}>{m.weight}</span>
                </div>
                <p style={{ fontSize: 13, color: 'var(--dgw-muted)', lineHeight: 1.6, margin: 0 }}>{m.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── How DepGuard compares (category table) ── */}
        <div style={{ marginBottom: 100 }}>
          <div className="dgw-section-header" style={{ marginBottom: 40 }}>
            <span className="dgw-section-tag" style={{ color: accentColor, background: `rgba(${accentRgb},.1)`, borderColor: `rgba(${accentRgb},.25)` }}>Positioning</span>
            <h2 style={{ color: 'var(--dgw-text)' }}>How DepGuard compares</h2>
            <p className="dgw-section-lead">
              No named competitors. Five tool categories that each solve part of the problem. DepGuard solves all of it.
            </p>
          </div>
          <div className="dgw-spec-table-wrap" style={{ borderRadius: 14, border: '1px solid rgba(255,255,255,.08)' }}>
            <table className="dgw-spec-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th style={{ color: accentColor }}>DepGuard</th>
                  <th>Alert Scanners</th>
                  <th>License Checkers</th>
                  <th>SBOM Validators</th>
                  <th>Cloud SCA</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map(row => (
                  <tr key={row.feature}>
                    <td style={{ fontWeight: 600 }}>{row.feature}</td>
                    <td style={{ color: accentColor, fontWeight: 600 }}>{row.depguard}</td>
                    <td style={{ color: 'var(--dgw-muted)' }}>{row.alertScanners}</td>
                    <td style={{ color: 'var(--dgw-muted)' }}>{row.licenseCheckers}</td>
                    <td style={{ color: 'var(--dgw-muted)' }}>{row.sbomValidators}</td>
                    <td style={{ color: 'var(--dgw-muted)' }}>{row.cloudSCA}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Technical Deep Dive (tabbed) ── */}
        <div style={{ marginBottom: 100 }}>
          <div className="dgw-section-header" style={{ marginBottom: 40 }}>
            <span className="dgw-section-tag" style={{ color: accentColor, background: `rgba(${accentRgb},.1)`, borderColor: `rgba(${accentRgb},.25)` }}>Under the hood</span>
            <h2 style={{ color: 'var(--dgw-text)' }}>Technical deep dive</h2>
            <p className="dgw-section-lead">
              For teams that want to understand the internals before committing.
            </p>
          </div>
          <div style={{ borderRadius: 14, border: '1px solid rgba(255,255,255,.08)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,.08)', flexWrap: 'wrap' }}>
              {DEEP_DIVE_TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    padding: '14px 24px', fontSize: 13, fontWeight: 700,
                    color: activeTab === tab.id ? accentColor : '#64748b',
                    background: activeTab === tab.id ? `rgba(${accentRgb},.08)` : 'transparent',
                    border: 'none', borderBottom: activeTab === tab.id ? `2px solid ${accentColor}` : '2px solid transparent',
                    cursor: 'pointer', transition: 'all .2s',
                    letterSpacing: '.3px',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div style={{ padding: '28px 32px' }}>
              {DEEP_DIVE_TABS.filter(t => t.id === activeTab).map(tab => (
                <p key={tab.id} style={{
                  fontSize: 15, color: 'var(--dgw-muted)', lineHeight: 1.8, margin: 0,
                  fontFamily: "'SF Mono','Fira Code',monospace", letterSpacing: '-.2px',
                }}>
                  {tab.content}
                </p>
              ))}
            </div>
          </div>
        </div>

        {/* ── Risk Scoring ── */}
        <div style={{ marginBottom: 100 }}>
          <div className="dgw-section-header" style={{ marginBottom: 40 }}>
            <span className="dgw-section-tag" style={{ color: accentColor, background: `rgba(${accentRgb},.1)`, borderColor: `rgba(${accentRgb},.25)` }}>Risk model</span>
            <h2 style={{ color: 'var(--dgw-text)' }}>Transparent scoring. No black boxes.</h2>
            <p className="dgw-section-lead">
              Every risk score is reproducible. The formula is public. You can calculate it by hand if you want to.
            </p>
          </div>
          <div style={{
            padding: '32px clamp(20px,4vw,48px)',
            borderRadius: 16,
            background: 'rgba(255,255,255,.025)',
            border: '1px solid rgba(255,255,255,.08)',
          }}>
            <div style={{
              fontFamily: "'SF Mono','Fira Code',monospace",
              fontSize: 14, color: 'var(--dgw-muted)', lineHeight: 2,
            }}>
              <div><span style={{ color: '#64748b' }}>// Vulnerability penalty</span></div>
              <div><span style={{ color: accentColor }}>penalty</span> = critical×20 + high×10 + medium×4 + low×1 <span style={{ color: '#64748b' }}>(capped at 100)</span></div>
              <div style={{ marginTop: 8 }}><span style={{ color: '#64748b' }}>// SBOM quality penalty</span></div>
              <div><span style={{ color: accentColor }}>qualPenalty</span> = (100 - qualityScore) / 4</div>
              <div style={{ marginTop: 8 }}><span style={{ color: '#64748b' }}>// Final risk score</span></div>
              <div><span style={{ color: accentColor }}>score</span> = 100 - penalty - qualPenalty</div>
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 24, flexWrap: 'wrap' }}>
              {[
                { label: 'LOW', range: '≥80', color: '#10b981' },
                { label: 'MEDIUM', range: '≥60', color: '#f59e0b' },
                { label: 'HIGH', range: '≥40', color: '#f97316' },
                { label: 'CRITICAL', range: '<40', color: '#ef4444' },
              ].map(r => (
                <div key={r.label} style={{
                  padding: '8px 16px', borderRadius: 10,
                  background: `${r.color}10`,
                  border: `1px solid ${r.color}40`,
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: r.color }}>{r.label}</span>
                  <span style={{ fontSize: 12, color: '#64748b' }}>{r.range}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Ecosystem Support ── */}
        <div style={{ marginBottom: 100 }}>
          <div className="dgw-section-header" style={{ marginBottom: 48 }}>
            <span className="dgw-section-tag" style={{ color: accentColor, background: `rgba(${accentRgb},.1)`, borderColor: `rgba(${accentRgb},.25)` }}>Ecosystems</span>
            <h2 style={{ color: 'var(--dgw-text)' }}>Seven ecosystems. One analysis pipeline.</h2>
            <p className="dgw-section-lead">
              Upload an SBOM from any supported ecosystem. DepGuard normalizes PURLs, resolves registries,
              and runs the same 16 dimensions regardless of language.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 16 }}>
            {ECOSYSTEMS.map(eco => (
              <div key={eco.name} style={{
                padding: '20px 22px', borderRadius: 14,
                background: `rgba(${accentRgb},.06)`,
                border: `1px solid rgba(${accentRgb},.25)`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 24 }}>{eco.icon}</span>
                  <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--dgw-text)' }}>{eco.name}</span>
                </div>
                <div style={{ fontSize: 12, color: accentColor, fontWeight: 600, marginBottom: 6 }}>
                  {eco.registry}
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                  <code style={{ fontSize: 11, background: 'rgba(255,255,255,.06)', padding: '1px 6px', borderRadius: 4 }}>{eco.manifest}</code>
                </div>
                <div style={{ fontSize: 13, color: 'var(--dgw-muted)', lineHeight: 1.5 }}>{eco.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── CLI / API Reference ── */}
        <div style={{ marginBottom: 100 }}>
          <div className="dgw-section-header" style={{ marginBottom: 40 }}>
            <span className="dgw-section-tag" style={{ color: accentColor, background: `rgba(${accentRgb},.1)`, borderColor: `rgba(${accentRgb},.25)` }}>API reference</span>
            <h2 style={{ color: 'var(--dgw-text)' }}>80+ endpoints. REST. Predictable.</h2>
            <p className="dgw-section-lead">
              Every analysis dimension is an API call. CI/CD integration is a single curl command.
            </p>
          </div>
          <div style={{
            background: '#0d1117', borderRadius: 12, overflow: 'hidden',
            border: '1px solid rgba(255,255,255,.08)',
            maxWidth: 800, margin: '0 auto',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
              background: '#161b22', borderBottom: '1px solid rgba(255,255,255,.07)',
            }}>
              {['#ef4444', '#f59e0b', '#10b981'].map(c => <div key={c} style={{ width: 8, height: 8, borderRadius: '50%', background: c }} />)}
              <span style={{ fontSize: 11, color: '#4a607a', marginLeft: 4, fontFamily: 'monospace' }}>terminal</span>
            </div>
            <div style={{ padding: '16px 20px', fontFamily: 'monospace', fontSize: 13, lineHeight: 2 }}>
              {CLI_COMMANDS.map((cmd, i) => (
                <div key={i}>
                  <span style={{ color: '#4a607a' }}>{'# '}{cmd.purpose}</span>
                  <br />
                  <span style={{ color: '#4a607a' }}>$ </span>
                  <span style={{ color: '#e6edf3' }}>{cmd.cmd}</span>
                  {i < CLI_COMMANDS.length - 1 && <br />}
                </div>
              ))}
              <br />
              <span style={{ color: '#3fb950' }}>{'{'}"passed": false, "score": 62, "violations": [...]{'}'}</span>
            </div>
          </div>
        </div>

        {/* ── Features grid (all capabilities) ── */}
        <div style={{ marginBottom: 100 }}>
          <div className="dgw-section-header" style={{ marginBottom: 48 }}>
            <span className="dgw-section-tag" style={{ color: accentColor, background: `rgba(${accentRgb},.1)`, borderColor: `rgba(${accentRgb},.25)` }}>Full feature list</span>
            <h2 style={{ color: 'var(--dgw-text)' }}>Everything in one binary</h2>
          </div>
          <div className="dgw-feature-grid">
            {features.map(f => (
              <div key={f.title} className="dgw-feature-item">
                <div className="dgw-feature-item-icon">{f.icon}</div>
                <div className="dgw-feature-item-title">{f.title}</div>
                <div className="dgw-feature-item-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── CTA ── */}
        <div style={{
          padding: 56, borderRadius: 20,
          background: `linear-gradient(135deg, rgba(${accentRgb},.1), rgba(${accentRgb},.04))`,
          border: `1px solid rgba(${accentRgb},.2)`,
          textAlign: 'center',
          marginBottom: 0,
        }}>
          <h3 style={{ fontSize: 32, fontWeight: 800, color: 'var(--dgw-text)', marginBottom: 12, letterSpacing: '-.5px' }}>
            60 seconds from upload to full risk report. Try it.
          </h3>
          <p style={{ color: 'var(--dgw-muted)', marginBottom: 32, fontSize: 16, maxWidth: 520, margin: '0 auto 32px' }}>
            Single Go binary. Embedded SQLite. Zero infrastructure.
            Upload an SBOM and see every dimension before your coffee gets cold.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/trial?plan=professional&product=depguard" className="dgw-btn dgw-btn-primary dgw-btn-lg">Start free trial →</Link>
            <Link to="/website/product/prism" className="dgw-btn dgw-btn-ghost dgw-btn-lg">◈ See PRISM</Link>
            <Link to="/website/product/vigil" className="dgw-btn dgw-btn-ghost dgw-btn-lg">👁️ See Vigil</Link>
          </div>
        </div>
      </div>

      {/* ── For Decision Makers ── */}
      <section style={{ padding: '80px clamp(20px,5vw,60px)', background: '#0a0e17' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <span style={{
              display: 'inline-block', background: `rgba(${accentRgb},.12)`,
              border: `1px solid rgba(${accentRgb},.3)`, borderRadius: 100,
              padding: '4px 14px', fontSize: 11, fontWeight: 700,
              color: accentColor, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16,
            }}>
              Outcomes
            </span>
            <h2 style={{ fontSize: 'clamp(28px,4vw,44px)', fontWeight: 900, color: '#f0f6fc', marginBottom: 16, letterSpacing: '-0.5px' }}>
              For decision makers
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 24 }}>
            {DECISION_CARDS.map(d => (
              <div key={d.big} style={{
                padding: '36px 28px', borderRadius: 16, textAlign: 'center',
                background: `rgba(${accentRgb},.04)`,
                border: `1px solid rgba(${accentRgb},.15)`,
              }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>{d.icon}</div>
                <div style={{ fontSize: 28, fontWeight: 900, color: accentColor, marginBottom: 16, letterSpacing: '-1px', lineHeight: 1.1 }}>{d.big}</div>
                <p style={{ fontSize: 15, color: '#8b949e', lineHeight: 1.7, margin: 0 }}>{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Part of the Lexint Platform ── */}
      <section style={{ padding: '80px clamp(20px,5vw,60px)', background: '#0d1117' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
          {/* PRISM */}
          <div style={{ background: 'rgba(6,182,212,.06)', border: '1px solid rgba(6,182,212,.2)', borderRadius: 16, padding: '36px 32px' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>◈</div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#0891b2', marginBottom: 8 }}>Code Intelligence</div>
            <h3 style={{ fontSize: 24, fontWeight: 800, color: '#e6edf3', marginBottom: 12, lineHeight: 1.25 }}>PRISM</h3>
            <p style={{ fontSize: 15, color: '#8b949e', lineHeight: 1.7, marginBottom: 24 }}>
              The engine underneath. 5-layer Code Property Graph. 179 rules across 9 languages.
              38ms cold start. Security, architecture, knowledge, trends — all from one graph.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
              {['CPG engine', '9 languages', '179 rules', '38ms start'].map(tag => (
                <span key={tag} style={{ background: 'rgba(6,182,212,.1)', border: '1px solid rgba(6,182,212,.25)', borderRadius: 100, padding: '3px 10px', fontSize: 12, color: '#0891b2', fontWeight: 600 }}>{tag}</span>
              ))}
            </div>
            <Link to="/website/product/prism" style={{
              display: 'inline-block', background: 'rgba(6,182,212,.12)',
              border: '1px solid rgba(6,182,212,.3)', borderRadius: 8,
              padding: '10px 20px', fontSize: 14, fontWeight: 700,
              color: '#06b6d4', textDecoration: 'none',
            }}>
              Explore PRISM →
            </Link>
          </div>

          {/* Vigil */}
          <div style={{ background: 'rgba(139,92,246,.06)', border: '1px solid rgba(139,92,246,.2)', borderRadius: 16, padding: '36px 32px' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>👁️</div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#8b5cf6', marginBottom: 8 }}>Static Analysis</div>
            <h3 style={{ fontSize: 24, fontWeight: 800, color: '#e6edf3', marginBottom: 12, lineHeight: 1.25 }}>Vigil</h3>
            <p style={{ fontSize: 15, color: '#8b949e', lineHeight: 1.7, marginBottom: 24 }}>
              SAST on the CPG. 140+ rules across 7 languages. SARIF output for GitHub, GitLab,
              and Azure DevOps. Write custom rules in PRL. Ships as a build step.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
              {['SAST', '140+ rules', 'SARIF', 'PRL/PQL'].map(tag => (
                <span key={tag} style={{ background: 'rgba(139,92,246,.1)', border: '1px solid rgba(139,92,246,.25)', borderRadius: 100, padding: '3px 10px', fontSize: 12, color: '#a78bfa', fontWeight: 600 }}>{tag}</span>
              ))}
            </div>
            <Link to="/website/product/vigil" style={{
              display: 'inline-block', background: 'rgba(139,92,246,.12)',
              border: '1px solid rgba(139,92,246,.3)', borderRadius: 8,
              padding: '10px 20px', fontSize: 14, fontWeight: 700,
              color: '#8b5cf6', textDecoration: 'none',
            }}>
              Explore Vigil →
            </Link>
          </div>

          {/* Platform */}
          <div style={{ background: `rgba(${accentRgb},.06)`, border: `1px solid rgba(${accentRgb},.2)`, borderRadius: 16, padding: '36px 32px' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🏛️</div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: accentColor, marginBottom: 8 }}>One Platform</div>
            <h3 style={{ fontSize: 24, fontWeight: 800, color: '#e6edf3', marginBottom: 12, lineHeight: 1.25 }}>Lexint</h3>
            <p style={{ fontSize: 15, color: '#8b949e', lineHeight: 1.7, marginBottom: 24 }}>
              Three products. Same data model. Same authentication. Same deployment.
              PRISM builds the graph. Vigil queries for code vulnerabilities. DepGuard queries for supply chain risk.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
              {['Unified platform', 'Shared CPG', 'Single deploy'].map(tag => (
                <span key={tag} style={{ background: `rgba(${accentRgb},.1)`, border: `1px solid rgba(${accentRgb},.25)`, borderRadius: 100, padding: '3px 10px', fontSize: 12, color: accentColor, fontWeight: 600 }}>{tag}</span>
              ))}
            </div>
            <Link to="/website" style={{
              display: 'inline-block', background: `rgba(${accentRgb},.12)`,
              border: `1px solid rgba(${accentRgb},.3)`, borderRadius: 8,
              padding: '10px 20px', fontSize: 14, fontWeight: 700,
              color: accentColor, textDecoration: 'none',
            }}>
              Visit lexint.io →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
